import * as React from 'react';
import { cn } from '../../lib/cn';

/**
 * PageHeader — layout wrapper combining breadcrumb + PageHero + action buttons
 * into a single responsive row.
 *
 * Matches the page-header pattern from the design HTML (quotes.html lines
 * 318–376): breadcrumb nav at top, hero text on the left, action buttons on
 * the right.
 *
 * Usage:
 *   <PageHeader
 *     breadcrumb={<Breadcrumb items={[...]} />}
 *     hero={<PageHero kicker="Sales · Quotes" title={<>Quotes…</>} />}
 *     actions={<><Button>Export</Button><Button>New quote</Button></>}
 *   />
 */
export interface PageHeaderProps extends React.HTMLAttributes<HTMLDivElement> {
  /** Optional breadcrumb navigation (rendered above hero) */
  breadcrumb?: React.ReactNode;
  /** Page title / hero section — required */
  hero: React.ReactNode;
  /** Optional action buttons rendered top-right */
  actions?: React.ReactNode;
}

export const PageHeader = React.forwardRef<HTMLDivElement, PageHeaderProps>(
  ({ className, breadcrumb, hero, actions, ...props }, ref) => (
    <div ref={ref} className={cn('mb-5', className)} {...props}>
      {breadcrumb && <div className="mb-3">{breadcrumb}</div>}
      <div className="flex flex-col gap-4 sm:flex-row sm:flex-wrap sm:items-start sm:justify-between">
        <div className="min-w-0 flex-1">{hero}</div>
        {actions && (
          <div className="flex w-full flex-col gap-2 max-sm:[&>*]:w-full sm:w-auto sm:shrink-0 sm:flex-row sm:items-center">
            {actions}
          </div>
        )}
      </div>
    </div>
  ),
);
PageHeader.displayName = 'PageHeader';

// ── PageHero helper sub-component ────────────────────────────────────────────

/**
 * PageHero — the kicker + title + description block inside a PageHeader.
 * Consumers can use this directly or pass arbitrary `hero` content to
 * PageHeader.
 *
 * Prop aliases: `dek` mirrors `description`; `title_accent` renders a serif
 * italic phrase after the title (design: "for Ceylon Trading.").
 * `actions` renders action buttons flush-right on the title row.
 */
export interface PageHeroProps extends Omit<
  React.HTMLAttributes<HTMLDivElement>,
  'title'
> {
  /** Small uppercase label above the title e.g. "Sales · Quotes" */
  kicker?: React.ReactNode;
  /** Primary heading — usually an `<h1>` or a string */
  title: React.ReactNode;
  /**
   * Optional italic accent word rendered in a serif font after the title.
   * Design: "as at", "for Ceylon Trading.", etc.
   */
  titleAccent?: string;
  /** Optional subtitle / description (alias: `dek`) */
  description?: React.ReactNode;
  /** Alias for `description` — accepted from story / design API */
  dek?: React.ReactNode;
  /** Optional action buttons rendered flush-right */
  actions?: React.ReactNode;
}

export const PageHero = React.forwardRef<HTMLDivElement, PageHeroProps>(
  (
    {
      className,
      kicker,
      title,
      titleAccent,
      description,
      dek,
      actions,
      ...props
    },
    ref,
  ) => {
    const subtitle = description ?? dek;
    return (
      <div
        ref={ref}
        className={cn(
          'flex flex-col gap-4 sm:flex-row sm:flex-wrap sm:items-start sm:justify-between',
          className,
        )}
        {...props}
      >
        <div className="min-w-0 flex-1">
          {kicker && (
            <div className="text-[11px] uppercase tracking-wider text-accent font-semibold mb-1">
              {kicker}
            </div>
          )}
          <h1 className="text-[32px] font-bold tracking-tighter leading-tight">
            {title}
            {titleAccent && (
              <span className="ml-2 font-serif italic font-normal text-ink-muted">
                {titleAccent}
              </span>
            )}
          </h1>
          {subtitle && (
            <p className="text-[13.5px] text-ink-muted mt-1.5 max-w-xl">
              {subtitle}
            </p>
          )}
        </div>
        {actions && (
          <div className="flex w-full flex-col gap-2 max-sm:[&>*]:w-full sm:w-auto sm:shrink-0 sm:flex-row sm:items-center">
            {actions}
          </div>
        )}
      </div>
    );
  },
);
PageHero.displayName = 'PageHero';
